import { Button } from '@fsd/ui';
import { Link } from '@tanstack/react-router';
import { ArrowRight, Settings } from 'lucide-react';
import { ProductCard } from '@/components';
import { useProducts } from '@/hooks/useProducts';
import { useTranslation } from '@/i18n/hooks';
import { useInView } from '@/utils';

export function PartsSection() {
  const { t, locale } = useTranslation('home');
  const { products: parts, loading } = useProducts('parts');

  const [sectionRef, isSectionInView] = useInView({ threshold: 0.1 });
  const [headerRef, isHeaderInView] = useInView({ threshold: 0.2 });

  const displayParts = parts.slice(0, 3);

  return (
    <section
      ref={sectionRef as React.RefObject<HTMLElement>}
      id="parts"
      className="relative overflow-hidden bg-white py-24 sm:py-32 dark:bg-slate-900"
    >
      {/* Premium Background Decorations */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Mesh Gradient with Accent color */}
        <div className="absolute top-[10%] -right-[10%] h-[70%] w-[70%] rounded-full bg-accent/5 blur-[120px] dark:bg-accent/10"></div>
        <div className="absolute -bottom-[20%] -left-[10%] h-[60%] w-[60%] rounded-full bg-primary/5 blur-[100px] dark:bg-primary/10"></div>
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div ref={headerRef as React.RefObject<HTMLDivElement>} className="mb-16 md:mb-24">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-bold uppercase tracking-widest mb-6">
                <Settings className="h-3.5 w-3.5 animate-spin-slow" />
                {t('parts.badge')}
              </div>
              <h2
                className={`text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl dark:text-slate-100 slide-in-left-ready ${isHeaderInView ? 'in-view' : ''}`}
              >
                {t('parts.title')}
              </h2>
              <p
                className={`mt-6 text-xl leading-relaxed text-slate-600 dark:text-slate-400 fade-in-ready ${isHeaderInView ? 'in-view' : ''}`}
                style={{ transitionDelay: '200ms' }}
              >
                {t('parts.subtitle')}
              </p>
            </div>

            <div
              className={`fade-in-ready ${isHeaderInView ? 'in-view' : ''}`}
              style={{ transitionDelay: '400ms' }}
            >
              <Link to="/catalog" search={{ category: 'parts', page: 1 }}>
                <Button
                  size="lg"
                  variant="outline"
                  className="group rounded-full px-8 py-6 text-base font-bold transition-all hover:bg-accent hover:text-white hover:border-accent hover:scale-105 active:scale-95"
                >
                  {t('parts.viewCatalog')}
                  <ArrowRight className="ml-3 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div className="relative">
          {loading ? (
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-[520px] w-full animate-pulse rounded-[2.5rem] bg-slate-100 dark:bg-slate-800/50"
                />
              ))}
            </div>
          ) : parts.length === 0 ? (
            <div className="relative overflow-hidden rounded-[3rem] border border-slate-200 bg-slate-50/50 p-12 text-center dark:border-slate-800 dark:bg-slate-900/50 sm:p-24">
              <div className="relative mx-auto flex h-24 w-24 items-center justify-center rounded-4xl bg-white text-slate-400 shadow-xl dark:bg-slate-800">
                <Settings
                  className="h-12 w-12 animate-spin-slow"
                  role="img"
                  aria-label="Settings icon"
                />
              </div>
              <h3 className="relative mt-8 max-w-md mx-auto text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-100">
                {t('parts.noProducts')}
              </h3>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {displayParts.map((part, index) => (
                <div
                  key={part.id}
                  className={`fade-in-up-ready ${isSectionInView ? 'in-view' : ''}`}
                  style={{ transitionDelay: `${300 + index * 150}ms` }}
                >
                  <ProductCard drone={part} locale={locale} productType="parts" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
